import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { Course } from '../hooks/useCourseApi';
import CourseCard from '../components/course/CourseCard';
import CourseCardSkeleton from '../components/common/CourseCardSkeleton';
import StandaloneUnitsSection from '../components/unit/StandaloneUnitsSection';
import SEOHead from '../components/common/SEOHead';
import axios from 'axios';
import { BASE_URL } from '../apiConfig';
import { getTenantHeaders } from '../config/tenant';
import {
  BRAND_FULL_NAME,
  BRAND_KEYWORDS,
  BRAND_NAME,
  BRAND_TEACHER_NAME,
  GRADE_SEO,
  SITE_URL,
} from '../config/brand';
import { getCoursePath } from '../utils/courseSlug';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

function mapApiCourseToCourse(apiCourse: Record<string, unknown>): Course {
  return {
    id: String(apiCourse.id ?? apiCourse.Id ?? ''),
    courseName: String(apiCourse.courseName ?? apiCourse.CourseName ?? ''),
    term: String(apiCourse.term ?? apiCourse.Term ?? ''),
    active: Boolean(apiCourse.active ?? apiCourse.Active ?? true),
    price: Number(apiCourse.price ?? apiCourse.Price ?? 0),
    grade: String(apiCourse.grade ?? apiCourse.Grade ?? ''),
    description: String(apiCourse.description ?? apiCourse.Description ?? ''),
    imagePath: String(apiCourse.imagePath ?? apiCourse.ImagePath ?? ''),
    modificationDate: String(apiCourse.modificationDate ?? apiCourse.ModificationDate ?? ''),
  };
}

function extractCoursesList(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  if (Array.isArray(raw.courses)) return raw.courses as Record<string, unknown>[];
  if (Array.isArray(raw.Courses)) return raw.Courses as Record<string, unknown>[];
  if (Array.isArray(raw.data)) return raw.data as Record<string, unknown>[];
  if (Array.isArray(raw.Data)) return raw.Data as Record<string, unknown>[];
  return [];
}

const GradeCoursesPage: React.FC = () => {
  const { grade } = useParams<{ grade: string }>();
  const location = useLocation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reloadRef = useRef<() => void>(() => undefined);

  // Get grade from params or location state
  const gradeValue = location.state?.grade || 
    (grade?.toLowerCase() === 'secondary1' ? 'Secondary1' : 
     grade?.toLowerCase() === 'secondary2' ? 'Secondary2' : 
     grade?.toLowerCase() === 'secondary3' ? 'Secondary3' : 'Secondary1');

  const gradeTitle = 
    gradeValue === 'Secondary1' ? 'الصف الأول الثانوي' :
    gradeValue === 'Secondary2' ? 'الصف الثاني الثانوي' :
    gradeValue === 'Secondary3' ? 'الصف الثالث الثانوي' : 'الصف الأول الثانوي';

  // Pinned course ID for Secondary3 grade page
  const PINNED_COURSE_ID = '1';

  const subjectName = location.state?.subjectName;
  const teacherId = location.state?.teacherId;
  const fromSubject = location.state?.fromSubject;
  const fromStudentPortal = location.state?.fromStudentPortal;

  // Public Course/tree — works for guests (no auth axios / no login redirect)
  const ensurePinnedSecondary3Course = useCallback(
    async (currentCourses: Course[]): Promise<Course[]> => {
      if (gradeValue !== 'Secondary3') {
        return currentCourses;
      }

      const existingPinned = currentCourses.find((c) => c.id === PINNED_COURSE_ID);
      if (existingPinned) {
        const others = currentCourses.filter((c) => c.id !== PINNED_COURSE_ID);
        return [existingPinned, ...others];
      }

      try {
        const response = await axios.get<ApiResponse<Course>>(`${BASE_URL}api/Course/tree`, {
          params: { courseid: PINNED_COURSE_ID },
          headers: { ...getTenantHeaders() },
        });
        if (response.data?.success && response.data.data) {
          return [mapApiCourseToCourse(response.data.data as unknown as Record<string, unknown>), ...currentCourses];
        }
      } catch (e) {
        console.error('Error fetching pinned Secondary3 course:', e);
      }

      return currentCourses;
    },
    [gradeValue]
  );

  /** Public teacher/subject courses — used after grades page for guests + auth */
  const fetchTeacherCourses = useCallback(
    async (finalTeacherId: string, finalSubjectId?: string) => {
      try {
        setIsLoading(true);
        setError(null);

        const params: Record<string, string | number | boolean> = {
          pageNumber,
          pageSize: 10,
          teacherid: finalTeacherId,
          active: true,
        };
        if (finalSubjectId) {
          params.subjectid = finalSubjectId;
        }

        const response = await axios.get<ApiResponse<unknown>>(`${BASE_URL}api/Course/all`, {
          params,
          headers: { ...getTenantHeaders() },
        });

        if (response.data?.success && response.data?.data) {
          let mappedCourses = extractCoursesList(response.data.data).map(mapApiCourseToCourse);

          // When browsing a grade with multiple subjects, keep only this grade's courses
          if (!finalSubjectId) {
            mappedCourses = mappedCourses.filter(
              (c) => !c.grade || c.grade.toLowerCase() === String(gradeValue).toLowerCase()
            );
          }

          const coursesWithPinned = await ensurePinnedSecondary3Course(mappedCourses);
          setCourses(coursesWithPinned);

          const raw = response.data.data as Record<string, unknown>;
          if (typeof raw?.totalPages === 'number') {
            setTotalPages(raw.totalPages);
          } else if (typeof raw?.totalCount === 'number') {
            setTotalPages(Math.max(1, Math.ceil(raw.totalCount / 10)));
          } else {
            setTotalPages(1);
          }
        } else {
          setCourses([]);
          setTotalPages(1);
          setError('لا توجد كورسات متاحة');
        }
      } catch (err) {
        console.error('Error fetching teacher courses:', err);
        setError('حدث خطأ أثناء جلب الكورسات');
        setCourses([]);
        setTotalPages(1);
      } finally {
        setIsLoading(false);
      }
    },
    [pageNumber, gradeValue, ensurePinnedSecondary3Course]
  );

  /** Public grade listing — no auth required so guests can browse after grades */
  const fetchGradeCourses = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axios.get<ApiResponse<{
        totalPages?: number;
        courses?: Course[];
      }>>(`${BASE_URL}api/AdminStudent/CourseActive`, {
        params: {
          grade: gradeValue,
          pagenumber: pageNumber,
          pagesize: 10,
        },
        headers: { ...getTenantHeaders() },
      });

      if (response.data?.success && response.data?.data) {
        const fetchedCourses = (response.data.data.courses || []).map((c) =>
          mapApiCourseToCourse(c as unknown as Record<string, unknown>)
        );
        const coursesWithPinned = await ensurePinnedSecondary3Course(fetchedCourses);
        setCourses(coursesWithPinned);
        setTotalPages(response.data.data.totalPages || 1);
      } else {
        setCourses([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching grade courses:', err);
      setError('حدث خطأ أثناء جلب الكورسات');
      setCourses([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [gradeValue, pageNumber, ensurePinnedSecondary3Course]);

  // Main effect — public fetch so unauthorized users can open courses after grades
  useEffect(() => {
    const state = location.state as Record<string, unknown> | null;
    let stateSubjectId = state?.subjectId as string | undefined;
    const stateFromSubject = state?.fromSubject;
    const stateTeacherId = state?.teacherId as string | undefined;
    const subjects = state?.subjects;

    if (
      !stateSubjectId &&
      Array.isArray(subjects) &&
      subjects.length === 1 &&
      subjects[0] &&
      typeof subjects[0] === 'object'
    ) {
      stateSubjectId = String((subjects[0] as { id?: string }).id ?? '');
    }

    const shouldUseSubject =
      Boolean(stateSubjectId) &&
      Boolean(stateFromSubject || (Array.isArray(subjects) && subjects.length === 1));

    const load = () => {
      if (stateTeacherId) {
        fetchTeacherCourses(stateTeacherId, shouldUseSubject ? stateSubjectId : undefined);
      } else {
        fetchGradeCourses();
      }
    };

    reloadRef.current = load;
    load();
  }, [location.pathname, location.key, location.state, gradeValue, pageNumber, fetchTeacherCourses, fetchGradeCourses]);

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const gradeSlug =
    grade?.toLowerCase() ||
    (gradeValue === 'Secondary2'
      ? 'secondary2'
      : gradeValue === 'Secondary3'
        ? 'secondary3'
        : 'secondary1');
  const gradeSeo = GRADE_SEO[gradeSlug];
  const seoPath = `/courses/grade/${gradeSlug}`;
  const seoTitle = `كورسات ${gradeSeo?.title || gradeTitle} أونلاين | ${BRAND_NAME} | ${BRAND_TEACHER_NAME}`;
  const seoDescription =
    `تصفّح واشترك في كورسات ${gradeSeo?.title || gradeTitle} على منصة نهج الأزهر مع الأستاذ سعيد الفقي — لغة عربية وعلوم شرعية أونلاين لطلاب الثانوية الأزهرية.` +
    (courses.length > 0 ? ` متاح حالياً ${courses.length} كورس.` : '');
  const seoKeywords = [
    BRAND_KEYWORDS,
    gradeSeo?.keywords,
    `كورسات ${gradeTitle}`,
    'كورسات أزهرية أونلاين',
    BRAND_TEACHER_NAME,
  ]
    .filter(Boolean)
    .join(', ');

  const gradeStructuredData = useMemo(() => {
    const collectionPage = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}${seoPath}#webpage`,
      name: seoTitle,
      description: seoDescription,
      url: `${SITE_URL}${seoPath}`,
      inLanguage: 'ar',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: [
        { '@type': 'Thing', name: gradeSeo?.title || gradeTitle },
        { '@type': 'Thing', name: 'نهج الأزهر' },
        { '@type': 'Thing', name: BRAND_TEACHER_NAME },
      ],
      provider: {
        '@type': 'EducationalOrganization',
        name: BRAND_FULL_NAME,
        url: SITE_URL,
      },
    };

    if (courses.length === 0) return collectionPage;

    const itemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `كورسات ${gradeSeo?.title || gradeTitle}`,
      numberOfItems: courses.length,
      itemListElement: courses.slice(0, 20).map((course, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${SITE_URL}${getCoursePath(course)}`,
        name: course.courseName,
        item: {
          '@type': 'Course',
          name: course.courseName,
          description: course.description || `كورس ${course.courseName} على ${BRAND_NAME}`,
          url: `${SITE_URL}${getCoursePath(course)}`,
          provider: {
            '@type': 'EducationalOrganization',
            name: BRAND_FULL_NAME,
            url: SITE_URL,
          },
          offers: {
            '@type': 'Offer',
            price: course.price ?? 0,
            priceCurrency: 'EGP',
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}${getCoursePath(course)}`,
          },
        },
      })),
    };

    return [collectionPage, itemList];
  }, [courses, gradeSeo?.title, gradeTitle, seoDescription, seoPath, seoTitle]);

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        url={seoPath}
        breadcrumbs={[
          { name: 'الكورسات', path: seoPath },
          { name: gradeSeo?.title || gradeTitle, path: seoPath },
        ]}
        structuredData={gradeStructuredData}
      />
      {/* Header Section */}
      <div className="py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-gray-500 mb-8" dir="ltr">
            {isLoading ? (
              <div className="flex items-center space-x-2 w-full">
                <div className="h-4 w-20 bg-gray-200 rounded-md relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                </div>
                <div className="w-4 h-4 mx-2 bg-gray-200 rounded-full relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                </div>
                <div className="h-4 w-32 bg-gray-200 rounded-md relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                </div>
              </div>
            ) : (
              <>
                <Link 
                  to={fromStudentPortal ? '/student-portal' : '/'} 
                  className="hover:text-orange-500 transition-colors duration-200 flex items-center"
                >
                  {fromStudentPortal ? 'الحصص والكورسات' : 'الرئيسية'}
                  <ArrowRight className="w-4 h-4 mx-2" />
                </Link>
                <span className="text-gray-900">
                  {teacherId
                    ? fromSubject && subjectName
                      ? `كورسات وحصص ${subjectName}`
                      : `كورسات وحصص ${gradeTitle}`
                    : fromSubject && subjectName
                    ? `كورسات ${subjectName}`
                    : `كورسات ${gradeTitle}`}
                </span>
              </>
            )}
          </nav>

          {/* Page Title */}
          <div className="text-center">
            {isLoading ? (
              <div className="flex flex-col items-center">
                <div className="h-12 w-64 bg-gray-200 rounded-lg mb-4 mx-auto relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                </div>
                <div className="h-4 w-48 bg-gray-200 rounded-md mx-auto relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                </div>
              </div>
            ) : (
              <h1 className="text-4xl md:text-5xl font-cairo-bold text-gray-900 mb-4">
                {teacherId
                  ? fromSubject && subjectName
                    ? `كورسات وحصص ${subjectName}`
                    : `كورسات وحصص ${gradeTitle}`
                  : fromSubject && subjectName
                  ? `كورسات ${subjectName}`
                  : `كورسات ${gradeTitle}`}
              </h1>
            )}
          </div>
        </div>
      </div>

      {/* Courses Section */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading && (
            <div className="py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                {Array.from({ length: 6 }).map((_, index) => (
                  <CourseCardSkeleton key={index} />
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-md mx-auto shadow-sm">
                <p className="text-gray-800 font-cairo-medium mb-4">{error}</p>
                <button
                  type="button"
                  onClick={() => reloadRef.current()}
                  className="px-6 py-2 bg-white border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition-colors duration-200 font-cairo-medium"
                >
                  إعادة المحاولة
                </button>
              </div>
            </div>
          )}

          {!isLoading && !error && courses.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 font-cairo-medium">
                لا توجد كورسات متاحة لـ{gradeTitle} حالياً
              </p>
            </div>
          )}

          {!isLoading && !error && courses.length > 0 && (
            <>
              {/* Courses Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                {courses.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-10">
                  <div className="inline-flex rounded-lg border border-gray-200 divide-x rtl:divide-x-reverse">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 transition-colors duration-200 font-cairo-medium ${
                          pageNumber === page
                            ? 'bg-orange-50 text-orange-600'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {teacherId && (
        <StandaloneUnitsSection
          teacherId={teacherId}
          title="حصص المدرس"
          subtitle="حصص مستقلة يمكن شراؤها مباشرة بدون اشتراك في كورس كامل"
          className="py-8 sm:py-12 bg-gradient-to-b from-white to-gold-50/20 border-t border-gray-100"
        />
      )}
    </div>
  );
};

export default GradeCoursesPage;
